import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, IconButton, Drawer, List, ListItem, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logoWhiteImage from "url:src/assets/images/logo-white.png";
import logoPrimaryImage from "url:src/assets/images/logo-primary.png";
import { useDevice } from "src/hooks/media-query";
import { StyledButton } from "./StyledButton";
import { useLocation, useNavigate } from "react-router";
import { HEADER_HEIGHT, MAIN_MAX_WIDTH } from "src/styles/const";
import { useIsTopPage } from "src/hooks/useIsTopPage";

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMobile } = useDevice();
  const navigate = useNavigate();
  const location = useLocation();
  const isTopPage = useIsTopPage();

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // スクロール時の背景色変更
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuSections = [
    {
      title: "無料売却AI査定",
      items: [
        { label: "特徴", href: "/#features-section" },
        { label: "成約事例", href: "/#case-section" },
        { label: "売却の流れ", href: "/#sell-flow-section" },
        { label: "よくある質問", href: "/#faq-section" },
      ],
    },
    {
      title: "株式会社Propally",
      items: [
        { label: "会社概要", href: "https://propally.co.jp/#company", external: true },
        { label: "お問い合わせ", href: "mailto:info@propally.co.jp" },
        { label: "プライバシーポリシー", href: "https://propally.co.jp/privacypolicy.html", external: true },
      ],
    },
  ];

  return (
    <>
      <Stack
        component="header"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: !isTopPage || isScrolled ? "white" : "transparent",
          padding: "8px 20px",
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          height: `${HEADER_HEIGHT}px`,
          width: { xs: "100%", md: `${MAIN_MAX_WIDTH}px` },
          zIndex: 1000,
          transition: "background-color 0.3s ease",
          boxShadow: !isTopPage || isScrolled ? "0 2px 10px rgba(0,0,0,0.1)" : "none",
        }}
      >
        <Box
          component="img"
          src={!isTopPage || isScrolled ? logoPrimaryImage : logoWhiteImage}
          alt="Propally"
          sx={{
            width: "140px",
            transition: "opacity 0.3s ease",
          }}
        />

        {isMobile && (
          <IconButton
            onClick={toggleDrawer}
            sx={{
              color: !isTopPage || isScrolled ? "primary.dark" : "white",
              transition: "color 0.3s ease",
              padding: 0,
            }}
          >
            {drawerOpen ? <CloseIcon sx={{ fontSize: "48px" }} /> : <MenuIcon sx={{ fontSize: "48px" }} />}
          </IconButton>
        )}
      </Stack>

      {/* SP版フルスクリーンメニュー */}
      {isMobile && (
        <Drawer
          anchor="top"
          open={drawerOpen}
          onClose={toggleDrawer}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "100vh",
              backgroundColor: "primary.dark",
              display: "flex",
              flexDirection: "column",
              paddingTop: "72px",
            },
            zIndex: 999,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%", padding: "80px 52px" }}>
            {menuSections.map((section, sectionIndex) => (
              <Box key={sectionIndex} sx={{ marginBottom: "24px" }}>
                <Typography
                  sx={{
                    color: "white",
                    fontSize: "20px",
                    fontWeight: "bold",
                    paddingBottom: "8px",
                  }}
                >
                  {section.title}
                </Typography>
                <List sx={{ padding: 0 }}>
                  {section.items.map((item, itemIndex) => {
                    // 外部リンクやmailtoの場合はそのまま
                    if (item.external || item.href.startsWith("mailto:")) {
                      return (
                        <ListItem
                          key={itemIndex}
                          component="a"
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setDrawerOpen(false)}
                          sx={{
                            padding: "2px 0",
                            cursor: "pointer",
                            textDecoration: "none",
                            color: "inherit",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={item.label}
                            sx={{
                              lineHeight: 1,
                              color: "white",
                            }}
                          />
                        </ListItem>
                      );
                    }

                    // 内部リンク（ハッシュ付き）の場合はクエリ維持
                    const isHashLink = item.href.startsWith("/#");
                    const href = isHashLink
                      ? `${location.pathname}${location.search}${item.href.substring(1)}` // /#xxx → /currentpath?query#xxx
                      : item.href;

                    return (
                      <ListItem
                        key={itemIndex}
                        component="a"
                        href={href}
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                          padding: "2px 0",
                          cursor: "pointer",
                          textDecoration: "none",
                          color: "inherit",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          sx={{
                            lineHeight: 1,
                            color: "white",
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ))}
            <StyledButton
              fullWidth
              variant="contained"
              onClick={() => {
                setDrawerOpen(false);
                window.scrollTo(0, 0);
                navigate({
                  pathname: "/",
                  search: location.search,
                });
              }}
            >
              無料売却AI査定を試す
            </StyledButton>
          </Box>
        </Drawer>
      )}
    </>
  );
}
